import React, { useState } from "react";
import { useAuth } from "./AuthProvider";
import { supabase } from "./supabase/auth";
import { logActivity } from "./activityLog";
import roundsquareLogo from "./samyang-roundsquare-logo.png";
import LanguageToggle from "./LanguageToggle";
import { useTranslation } from 'react-i18next';

const REQUEST_ACCESS_MAILTO =
  "mailto:mmckeever@roundsquare.ai" +
  "?subject=" + encodeURIComponent("Please give access") +
  "&body=" + encodeURIComponent("I need to login.");

const REQUEST_SIGNIN_MAILTO =
  "mailto:mmckeever@roundsquare.ai,joana.aguiar@roundsquare.ai" +
  "?subject=" + encodeURIComponent("Request new sign-in link") +
  "&body=" + encodeURIComponent("I forgot my password. Please send a new sign-in link.");

const secondaryButtonStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid rgba(139,92,246,0.4)",
  background: "rgba(139,92,246,0.12)",
  color: "#c4b5fd",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  textAlign: "center",
  textDecoration: "none",
  marginTop: "12px",
  boxSizing: "border-box",
};

/* ── SVG Icons (KeyIcon used by password-recovery flow) ── */
const KeyIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

/* ── Animated background component ── */
export function WavyBackground() {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationId: number;
    let time = 0;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const waves = [
      { amplitude: 50, frequency: 0.003, speed: 0.02, yOffset: 0.35, thickness: 80, color: "rgba(139,92,246,0.6)" },
      { amplitude: 60, frequency: 0.004, speed: -0.015, yOffset: 0.45, thickness: 100, color: "rgba(236,72,153,0.5)" },
      { amplitude: 45, frequency: 0.005, speed: 0.025, yOffset: 0.55, thickness: 90, color: "rgba(6,182,212,0.55)" },
      { amplitude: 55, frequency: 0.003, speed: -0.018, yOffset: 0.5, thickness: 70, color: "rgba(168,85,247,0.45)" },
      { amplitude: 40, frequency: 0.006, speed: 0.012, yOffset: 0.6, thickness: 60, color: "rgba(249,115,22,0.35)" },
      { amplitude: 65, frequency: 0.002, speed: -0.01, yOffset: 0.4, thickness: 110, color: "rgba(52,211,153,0.3)" },
    ];
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 1;
      waves.forEach((wave) => {
        const baseY = canvas.height * wave.yOffset;
        ctx.beginPath();
        for (let x = 0; x <= canvas.width; x += 3) {
          const y =
            baseY +
            Math.sin(x * wave.frequency + time * wave.speed) * wave.amplitude +
            Math.sin(x * wave.frequency * 0.6 + time * wave.speed * 1.4) * (wave.amplitude * 0.3) +
            Math.cos(x * wave.frequency * 0.4 + time * wave.speed * 0.8) * (wave.amplitude * 0.2);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = wave.color;
        ctx.lineWidth = wave.thickness;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.shadowColor = wave.color;
        ctx.shadowBlur = 40;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

/* ── Shimmer title component ── */
function ShimmerTitle({ size = 44 }: { size?: number }) {
  return (
    <h1
      style={{
        fontSize: `${size}px`,
        fontWeight: 900,
        background:
          "linear-gradient(135deg, #c4b5fd 0%, #f0abfc 25%, #fcd34d 50%, #a78bfa 75%, #c4b5fd 100%)",
        backgroundSize: "300% 300%",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        animation: "shimmer 4s ease infinite",
        letterSpacing: "-2px",
        margin: 0,
        lineHeight: 1.1,
      }}
    >
      PROTEIN PAL
    </h1>
  );
}

/* ── Shared styles ── */
const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: "8px",
  border: "1px solid rgba(139,92,246,0.3)",
  background: "#0f0b2a",
  color: "#f8fafc",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
};

const purpleButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "none",
  background: disabled
    ? "#4c1d95"
    : "linear-gradient(135deg, #8b5cf6, #a78bfa)",
  color: "white",
  fontSize: "15px",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
});

const linkStyle: React.CSSProperties = {
  color: "#a78bfa",
  cursor: "pointer",
  fontWeight: 500,
  background: "none",
  border: "none",
  padding: 0,
  fontSize: "13px",
};

const cardBg: React.CSSProperties = {
  background: "rgba(15, 11, 42, 0.85)",
  borderRadius: "16px",
  padding: "48px 40px",
  width: "100%",
  maxWidth: "400px",
  boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
  border: "1px solid rgba(139,92,246,0.2)",
  backdropFilter: "blur(20px)",
};

const errorBox: React.CSSProperties = {
  background: "rgba(127,29,29,0.6)",
  border: "1px solid #991b1b",
  borderRadius: "8px",
  padding: "10px 14px",
  marginBottom: "16px",
  color: "#fca5a5",
  fontSize: "13px",
};

export default function Portal() {
  const { user, loading, recovering, clearRecovering } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPasswordField, setConfirmPasswordField] = useState("");
  const [fullName, setFullName] = useState("");
  const [view, setView] = useState<"login" | "signup">("login");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteChecking, setInviteChecking] = useState(false);

  const { t, i18n } = useTranslation();

  // Honor ?invite=<token> in the URL: prefill email, force signup view.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("invite");
    if (!token) return;
    setInviteChecking(true);
    (async () => {
      const { data, error: invErr } = await supabase
        .from("invitations")
        .select("email, expires_at, consumed_at, revoked_at")
        .eq("token", token)
        .maybeSingle();
      setInviteChecking(false);
      if (invErr || !data) {
        setError(t('portal.inviteInvalid'));
        return;
      }
      if (data.consumed_at) {
        setError(t('portal.inviteUsed'));
        return;
      }
      if (data.revoked_at) {
        setError(t('portal.inviteRevoked'));
        return;
      }
      if (data.expires_at && new Date(data.expires_at).getTime() < Date.now()) {
        setError(t('portal.inviteExpired'));
        return;
      }
      setEmail(data.email);
      setInviteToken(token);
      setView("signup");
    })();
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        logActivity("login_failed", { email });
        throw signInError;
      }
      logActivity("login_success", { email });
    } catch (err: any) {
      setError(err?.message || t('portal.authFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPasswordField) {
      setError(t('portal.passwordsDoNotMatch'));
      return;
    }

    if (!inviteToken) {
      setError(t('portal.signupRequiresLink'));
      return;
    }

    setSubmitting(true);
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: { data: { full_name: fullName.trim() || null } },
      });
      if (signUpError) throw signUpError;

      const { data: consumed } = await supabase.rpc("consume_invitation", {
        p_token: inviteToken,
        p_email: normalizedEmail,
      });
      if (!consumed) {
        setError(t('portal.inviteConsumeFailed'));
      }

      // Strip ?invite=<token> from the URL so a refresh doesn't re-validate
      // a now-consumed token.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.pathname + url.hash);
      } catch {}

      logActivity("signup", { email: normalizedEmail, via_invite: true });
      setSignUpSuccess(true);
    } catch (err: any) {
      setError(err?.message || t('portal.signUpFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(t('portal.passwordsDoNotMatch'));
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateError) throw updateError;
      setResetSuccess(true);
    } catch (err: any) {
      setError(err?.message || t('portal.updatePasswordFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    setView("login");
    setError("");
    setRequestSent(false);
    setSignUpSuccess(false);
    setPassword("");
    setConfirmPasswordField("");
    setFullName("");
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0618",
          color: "#a78bfa",
          fontFamily: FONT_STACK,
        }}
      >
        <WavyBackground />
        <span style={{ position: "relative", zIndex: 1 }}>{t('portal.loading')}</span>
      </div>
    );
  }

  /* ── Password recovery (from email link) ── */
  if (recovering) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_STACK,
          background: "#0a0618",
        }}
      >
        <WavyBackground />
        <div style={{ ...cardBg, position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <KeyIcon />
            <h2
              style={{
                color: "#f8fafc",
                fontSize: "22px",
                fontWeight: 700,
                margin: "12px 0 4px 0",
              }}
            >
              {t('portal.setNewPassword')}
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "14px", margin: 0 }}>
              {t('portal.enterNewPassword')}
            </p>
          </div>

          {resetSuccess ? (
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  color: "#4ade80",
                  fontWeight: 600,
                  fontSize: "16px",
                }}
              >
                {t('portal.passwordUpdated')}
              </p>
              <p style={{ color: "#94a3b8", fontSize: "14px" }}>
                {t('portal.passwordResetSuccess')}
              </p>
              <button
                onClick={() => {
                  clearRecovering();
                  window.location.hash = "";
                  window.location.reload();
                }}
                style={purpleButtonStyle(false)}
              >
                {t('portal.continue')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleUpdatePassword}>
              {error && <div style={errorBox}>{error}</div>}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    color: "#cbd5e1",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "6px",
                  }}
                >
                  {t('portal.newPassword')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('portal.minChars')}
                  required
                  minLength={6}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    color: "#cbd5e1",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: "6px",
                  }}
                >
                  {t('portal.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('portal.minChars')}
                  required
                  minLength={6}
                  style={inputStyle}
                />
              </div>
              <button type="submit" disabled={submitting} style={purpleButtonStyle(submitting)}>
                {submitting ? t('portal.updatingPassword') : t('portal.updatePassword')}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  /* ── Not authenticated: Login / Signup / Reset ── */
  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_STACK,
          padding: "24px",
          background: "#0a0618",
        }}
      >
        <WavyBackground />
        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "400px" }}>
          {/* ROUNDSQUARE branding */}
          <div style={{ textAlign: "center" }}>
            <img src={roundsquareLogo} alt="Samyang Roundsquare" style={{ height: "44px", width: "auto", marginBottom: "12px" }} />
          </div>

          <div style={cardBg}>
            {/* Language toggle */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
              <LanguageToggle />
            </div>
            {/* Title */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <ShimmerTitle size={36} />
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: "13px",
                  margin: "8px 0 0 0",
                }}
              >
                {view === "login" && t('portal.signInSubtitle')}
                {view === "signup" && t('portal.signUpSubtitle')}
              </p>
            </div>

            {/* ── LOGIN FORM ── */}
            {view === "login" && (
              <form onSubmit={handleSignIn}>
                {error && <div style={errorBox}>{error}</div>}
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('portal.emailPlaceholder')}
                    required
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.password')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('portal.minChars')}
                    required
                    minLength={6}
                    style={inputStyle}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  style={purpleButtonStyle(submitting)}
                >
                  {submitting ? t('portal.signingIn') : t('portal.signIn')}
                </button>
                <a
                  href={REQUEST_ACCESS_MAILTO}
                  style={secondaryButtonStyle}
                >
                  {t('portal.requestAccessButton')}
                </a>
                <a
                  href={REQUEST_SIGNIN_MAILTO}
                  style={secondaryButtonStyle}
                >
                  {t('portal.requestSigninButton')}
                </a>
              </form>
            )}

            {/* ── SIGNUP FORM ── */}
            {view === "signup" && !signUpSuccess && !requestSent && (
              <form onSubmit={handleSignUp}>
                {error && <div style={errorBox}>{error}</div>}
                {inviteToken && (
                  <div
                    style={{
                      background: "rgba(22,163,74,0.15)",
                      border: "1px solid rgba(22,163,74,0.4)",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      marginBottom: "16px",
                      color: "#86efac",
                      fontSize: "12px",
                      lineHeight: 1.5,
                    }}
                  >
                    {t('portal.validInviteBanner')}
                  </div>
                )}
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('portal.emailPlaceholder')}
                    required
                    readOnly={!!inviteToken}
                    style={{
                      ...inputStyle,
                      opacity: inviteToken ? 0.7 : 1,
                      cursor: inviteToken ? "not-allowed" : "text",
                    }}
                  />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.password')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('portal.minChars')}
                    required
                    minLength={6}
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={confirmPasswordField}
                    onChange={(e) => setConfirmPasswordField(e.target.value)}
                    placeholder={t('portal.minChars')}
                    required
                    minLength={6}
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      color: "#cbd5e1",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    {t('portal.whatToBeCalled')}
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t('portal.whatToBeCalledPlaceholder')}
                    style={inputStyle}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  style={purpleButtonStyle(submitting)}
                >
                  {submitting ? t('portal.signingUp') : t('portal.signUp')}
                </button>
                <p
                  style={{
                    textAlign: "center",
                    marginTop: "16px",
                    marginBottom: 0,
                  }}
                >
                  <button type="button" onClick={goToLogin} style={linkStyle}>
                    {t('portal.haveAccount')} {t('portal.signIn')}
                  </button>
                </p>
              </form>
            )}

            {/* Signup success (whitelisted) */}
            {view === "signup" && signUpSuccess && (
              <div style={{ textAlign: "center" }}>
                <p
                  style={{
                    color: "#4ade80",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {t('portal.accountCreated')}
                </p>
                <p style={{ color: "#94a3b8", fontSize: "14px", lineHeight: 1.6 }}>
                  {t('portal.accountCreatedDesc')}
                </p>
                <button
                  onClick={goToLogin}
                  style={{
                    ...linkStyle,
                    marginTop: "16px",
                    padding: "10px 24px",
                    border: "1px solid rgba(139,92,246,0.3)",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {t('portal.backToSignIn')}
                </button>
              </div>
            )}

            {/* Signup request sent (not whitelisted) */}
            {view === "signup" && requestSent && (
              <div style={{ textAlign: "center" }}>
                <p
                  style={{
                    color: "#fbbf24",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {t('portal.accessRequestSubmitted')}
                </p>
                <p style={{ color: "#94a3b8", fontSize: "14px", lineHeight: 1.6 }}>
                  {t('portal.accessRequestSubmittedDesc')}
                </p>
                <button
                  onClick={goToLogin}
                  style={{
                    ...linkStyle,
                    marginTop: "16px",
                    padding: "10px 24px",
                    border: "1px solid rgba(139,92,246,0.3)",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {t('portal.backToSignIn')}
                </button>
              </div>
            )}

          </div>

          {/* Credit line */}
          <p
            style={{
              textAlign: "center",
              color: "rgba(148,163,184,0.5)",
              fontSize: "11px",
              marginTop: "24px",
            }}
          >
            An R&D App by Meagan McKeever
          </p>
        </div>
      </div>
    );
  }

  /* All authenticated rendering now happens in Root.tsx via the unified nav.
     Portal only handles loading, password-recovery, and pre-auth flows. */
  return null;
}
